import React, { useState } from 'react';
import { Row, Col, Card, Statistic, Button, Table } from 'antd';
import dayjs from 'dayjs';
import PageContainer from '@/components/PageContainer';
import ChartCard from '@/components/ChartCard';
import { useRTTempPrice } from '@/api/rtTempPrice';
import { format2 } from '@/utils/charts';
import type { EChartsOption } from 'echarts';
import { BASE_DATAZOOM } from '@/utils/charts';
import { useIsMobile } from '@/hooks/useIsMobile';

const RtPricePage: React.FC = () => {
  const { data, isLoading, error, refetch } = useRTTempPrice();
  const [showTable, setShowTable] = useState(false);
  const isMobile = useIsMobile();

  const prices = data?.data?.prices ?? [];
  const kpi = data?.data?.kpi ?? {
    latest_price: 0,
    latest_time: '--:--',
    avg_price: 0,
    max_price: 0,
    min_price: 0,
  };

  const chartOption: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      valueFormatter: (value: any) => (typeof value === 'number' ? format2(value) : value),
    },
    toolbox: { show: false },
    dataZoom: BASE_DATAZOOM,
    grid: { left: 50, right: 20, top: 30, bottom: 85 },
    xAxis: {
      type: 'category',
      data: prices.map((p) => dayjs(p.target_time).format('HH:mm')),
      axisLabel: { fontSize: 11, color: '#9ca3af' },
    },
    yAxis: {
      type: 'value',
      name: '元/MWh',
      axisLabel: { fontSize: 11, color: '#9ca3af' },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
    },
    series: [
      {
        name: '实时临时电价',
        type: 'line',
        data: prices.map((p) => p.price_rt_temp_final),
        smooth: true,
        lineStyle: { color: '#0d9488', width: 2 },
        itemStyle: { color: '#0d9488' },
        showSymbol: false,
        connectNulls: true,
      },
    ],
  };

  const tableColumns = [
    {
      title: '时间',
      dataIndex: 'target_time',
      key: 'target_time',
      render: (v: string) => dayjs(v).format('HH:mm'),
    },
    {
      title: '实时临时电价 (元/MWh)',
      dataIndex: 'price_rt_temp_final',
      key: 'price_rt_temp_final',
      align: 'right' as const,
      render: (v: number) => v != null ? format2(v) : '-',
    },
  ];

  return (
    <PageContainer title="实时临时电价监控">
      {/* KPI Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="stretch">
        <Col xs={12} sm={6} style={{ display: 'flex' }}>
          <Card style={{ flex: 1 }} styles={{ body: { height: '100%' } }}>
            <Statistic
              title="最新价格"
              value={format2(kpi.latest_price)}
              suffix="元/MWh"
            />
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              最后更新: {kpi.latest_time}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6} style={{ display: 'flex' }}>
          <Card style={{ flex: 1 }} styles={{ body: { height: '100%' } }}>
            <Statistic title="今日均价" value={format2(kpi.avg_price)} suffix="元/MWh" />
          </Card>
        </Col>
        <Col xs={12} sm={6} style={{ display: 'flex' }}>
          <Card style={{ flex: 1 }} styles={{ body: { height: '100%' } }}>
            <Statistic title="今日最高" value={format2(kpi.max_price)} suffix="元/MWh" />
          </Card>
        </Col>
        <Col xs={12} sm={6} style={{ display: 'flex' }}>
          <Card style={{ flex: 1 }} styles={{ body: { height: '100%' } }}>
            <Statistic title="今日最低" value={format2(kpi.min_price)} suffix="元/MWh" />
          </Card>
        </Col>
      </Row>

      {/* Chart */}
      <ChartCard
        title="实时临时电价曲线"
        option={chartOption}
        loading={isLoading}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        height={isMobile ? 250 : 450}
      />

      {/* Raw Data Toggle */}
      <div style={{ marginTop: 16 }}>
        <Button onClick={() => setShowTable((s) => !s)}>
          {showTable ? '隐藏原始数据' : '查看原始数据'}
        </Button>
      </div>

      {/* Raw Data Table */}
      {showTable && (
        <div style={{ marginTop: 16 }}>
          <Table
            columns={tableColumns}
            dataSource={prices}
            rowKey={(r) => r.target_time}
            pagination={{ pageSize: 48 }}
            size="small"
            bordered
          />
        </div>
      )}
    </PageContainer>
  );
};

export default RtPricePage;
